import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { TextField } from "../shared/FormField";
import { apiClient, getApiErrorMessage, getFieldErrors } from "../../lib/apiClient";

const EMPTY_FORM = { item_id: "", name: "", price: "", quantity: "", status: 1 };

export function AddonFormDialog({ open, onOpenChange, addon, onSaved }) {
  const isEdit = Boolean(addon);
  const [form, setForm] = useState(EMPTY_FORM);
  const [itemOptions, setItemOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiClient.get("/items", { params: { limit: 500 } }).then(({ data }) => setItemOptions(data.items)).catch(() => {});
    setForm(addon ? {
      item_id: String(addon.item_id || ""), name: addon.name || "", price: addon.price ?? "",
      quantity: addon.quantity || "", status: addon.status ?? 1,
    } : EMPTY_FORM);
    setErrors({});
  }, [open, addon]);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    const payload = { ...form, item_id: Number(form.item_id), price: Number(form.price) || 0, status: Number(form.status) };
    try {
      if (isEdit) {
        await apiClient.put(`/addons/${addon.id}`, payload);
        toast.success("Addon updated");
      } else {
        await apiClient.post("/addons", payload);
        toast.success("Addon created");
      }
      onSaved();
    } catch (error) {
      setErrors(getFieldErrors(error));
      toast.error(getApiErrorMessage(error, "Could not save addon."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="addon-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Addon" : "New Addon"}</DialogTitle>
          <DialogDescription>An optional extra tied to a menu item (e.g. Extra Roti) that a customer can add on.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="addon-form-item">Base Item</Label>
            <Select value={form.item_id} onValueChange={(v) => setForm((f) => ({ ...f, item_id: v }))}>
              <SelectTrigger id="addon-form-item" data-testid="addon-form-item"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {itemOptions.map((it) => <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.item_id && <p className="text-xs text-danger">{errors.item_id}</p>}
          </div>
          <TextField label="Name" required value={form.name} onChange={setField("name")} error={errors.name} testId="addon-form-name" />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Price" required value={form.price} onChange={setField("price")} error={errors.price} testId="addon-form-price" />
            <TextField
              label="Quantity" required value={form.quantity} onChange={setField("quantity")} error={errors.quantity}
              placeholder="e.g. 5" hint="How many of the base item per order." testId="addon-form-quantity"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addon-form-status">Status</Label>
            <Select value={String(form.status)} onValueChange={(v) => setForm((f) => ({ ...f, status: Number(v) }))}>
              <SelectTrigger id="addon-form-status" data-testid="addon-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Active</SelectItem>
                <SelectItem value="2">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} data-testid="addon-form-cancel-button">Cancel</Button>
            <Button type="submit" variant="accent" disabled={saving || !form.item_id} data-testid="addon-form-submit-button">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Addon"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
